/**
 * 渲染风格初始化 Hook
 *
 * 职责：
 * - 记住用户上次选择的渲染风格（localStorage）
 * - 首次使用 / 记忆失效时，回退到官方默认模型（DEFAULT_RENDER_MODEL_ID）
 * - 通过 /api/model/detail 按 id 查询模型，替代旧的 /api/renderStyleTemplate/list
 * - 历史数据兼容：旧版 styleType(0/1/空) → 新版 modelId + styleType
 */
import { objExt } from '@xmiles/holopix-helper'
import { ModuleId } from '@xmiles/holopix-types'
import { message } from 'ant-design-vue'
import { queryModelDetails, queryModelIdByTemplateId } from '@/components/Model/service'
import type { UniversalRenderingModelItem } from '@/components/Model/service'
import { logger } from '@/utils/logger'
import { DEFAULT_RENDER_MODEL_ID } from './constants'

/**
 * 渲染风格列表查询 Key
 * 全局统一使用，确保缓存一致性
 */
export const RENDER_STYLE_LIST_QUERY_KEY = ['universalRenderingModelList'] as const;

export const LAST_RENDER_STYLE_STORAGE_KEY = 'xmiles:render:last_style';

/**
 * 存储的渲染风格数据结构
 *
 * 关键背景：
 * - IUGCModelState.id 是"发布作品 id"（publish id），与 /api/model/detail 使用的
 *   训练模型 id 属于不同 id 空间。对于多版本模型，二者必定不同。
 * - 因此需要在 localStorage 中存储足够的展示信息（name、preview、verList），
 *   恢复时仅用 API 做存在性校验，展示信息从存储数据中取。
 *
 * selectedVersionId：多版本模型场景下用户选择的具体版本 modelId（训练模型 id）
 */
interface StoredRenderStyle {
  id?: number;
  name?: string;
  type?: number;
  selectedVersionId?: number;
  preview?: string;
  verList?: { modelId: number; verName: string; loraLabel?: number }[];
}

/**
 * 读取本地存储的渲染风格完整数据
 * 兼容旧格式（仅存储 { name }）
 */
export function readStoredRenderStyle(): StoredRenderStyle | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(LAST_RENDER_STYLE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = objExt.parse<StoredRenderStyle>(raw, {});
    const name = parsed?.name;
    if (typeof name !== 'string' || name.length === 0) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * 存储渲染风格数据到本地
 * 存储完整信息：id、name、type，用于精确恢复
 */
export function writeStoredRenderStyle(data: StoredRenderStyle | null | undefined) {
  if (typeof window === 'undefined') return;
  try {
    if (!data?.name) {
      localStorage.removeItem(LAST_RENDER_STYLE_STORAGE_KEY);
      return;
    }
    localStorage.setItem(LAST_RENDER_STYLE_STORAGE_KEY, objExt.stringify(data));
  } catch {
    return;
  }
}

/**
 * 记住用户选择的渲染风格
 *
 * 兼容两种数据源：
 * - IUGCModelState（modelPublishList）：使用 modelName / kleinFluxType
 * - ModelDetailsState（model/detail）：使用 modelName / loraType
 */
export function rememberRenderStyle(style: any) {
  const name = style?.modelName ?? style?.name;
  if (name == null) return;
  const type = style?.kleinFluxType ?? style?.type;
  const versionId = style?.selectedVersionId ?? style?._selectedVersionId;
  const preview = style?.preview ?? style?.url;
  const verList = Array.isArray(style?.verList) && style.verList.length > 0
    ? style.verList.map((v: any) => ({ modelId: v.modelId, verName: v.verName, loraLabel: v.loraLabel }))
    : undefined;
  const data: StoredRenderStyle = {
    id: style?.id,
    name: String(name),
    type: type != null ? Number(type) : undefined,
    selectedVersionId: versionId != null ? Number(versionId) : undefined,
    preview: preview != null ? String(preview) : undefined,
    verList,
  };
  writeStoredRenderStyle(data);
}

/**
 * 通过 /api/model/detail 验证指定 id 的模型是否有效
 * @returns 有效时返回模型数据，无效时返回 null
 */
async function fetchValidModelById(id: number): Promise<any | null> {
  try {
    const { data } = await queryModelDetails({ id });
    if (!data?.id || (data as any).deleteStatus === 1) return null;
    return data;
  } catch (err) {
    logger.error('queryModelDetails 接口异常', { api: '/api/model/detail', params: { id }, error: err });
    return null;
  }
}

/**
 * 获取官方默认渲染风格（DEFAULT_RENDER_MODEL_ID）
 */
export async function getDefaultRenderStyle() {
  return fetchValidModelById(DEFAULT_RENDER_MODEL_ID);
}

/**
 * 获取用户偏好的渲染风格
 *
 * 优先级：
 * 1. 上次选择的风格（通过 localStorage 记忆），按 id 查询验证是否仍有效
 * 2. 如果记忆的风格不存在（已下架/删除），回退到官方默认模型
 */
export async function getPreferredRenderStyle() {
  const stored = readStoredRenderStyle();

  if (stored?.id != null) {
    // publish id ≠ training model id，优先用 selectedVersionId 或 verList 首项的 modelId 查询
    const queryId = stored.selectedVersionId
      ?? stored.verList?.[0]?.modelId
      ?? stored.id;
    const hit = await fetchValidModelById(queryId);

    if (hit) {
      // /api/model/detail 返回的是训练模型数据，modelName 是训练模型名（如 "v2.0"），
      // 而非发布模型名。需要恢复正确的展示信息：
      // 优先级：modelPublishName > stored.name > hit.modelName
      hit.modelName = hit.modelPublishName ?? stored.name ?? hit.modelName;
      hit.preview = hit.modelPublishPreview ?? stored.preview ?? hit.preview;
      // 恢复 publish id（hit.id 可能是训练模型 id，需要改回 publish id）
      hit.id = stored.id;
      // 恢复 verList（/api/model/detail 不返回此字段）
      if (Array.isArray(stored.verList) && stored.verList.length > 0) {
        hit.verList = stored.verList;
      }
      // 恢复版本选择
      if (stored.selectedVersionId != null) {
        hit.selectedVersionId = stored.selectedVersionId;
      }

      rememberRenderStyle(hit);
      return hit;
    }
  }

  // 无记忆 / 记忆的模型已失效 → 使用默认模型
  const def = await getDefaultRenderStyle();
  if (def?.id != null) rememberRenderStyle(def);
  return def;
}

/**
 * 验证当前已选中的风格是否仍然有效
 * - 如果有效：更新记忆，返回 true
 * - 如果无效：尝试按记忆 id 回退，再失败就回退到默认模型，返回 false
 */
export async function ensureValidSelectedStyle(params: any) {
  const currentId = params?.selectStyle?.id ?? params?.styleId;

  if (currentId != null) {
    const current = await fetchValidModelById(currentId);
    if (current) {
      rememberRenderStyle(current);
      return true;
    }
  }

  // 当前选中的风格无效，尝试按 localStorage 记忆回退
  const stored = readStoredRenderStyle();
  if (stored?.id != null && stored.id !== currentId) {
    const byStored = await fetchValidModelById(stored.id);
    if (byStored?.id) {
      params.selectStyle = byStored;
      params.styleId = byStored.id;
      rememberRenderStyle(byStored);
      return false;
    }
  }

  // 均失效 → 使用默认模型
  const def = await getDefaultRenderStyle();
  if (def?.id) {
    params.selectStyle = def;
    params.styleId = def.id;
    rememberRenderStyle(def);
  }
  return false;
}

// ──────────────────────────────────────────────────────
// 历史数据兼容：旧版 styleType(0/1/空) → 新模型体系
// ──────────────────────────────────────────────────────

/** loraType 到内部 type 的映射 */
const LORA_TYPE_TO_INTERNAL: Record<string, number> = { Klein: 0, KleinFlux: 1, KleinFlux2: 2 };

/**
 * 判断 styleType 是否为旧版历史数据
 * 旧版 styleType 只有 0（普通）、1（高级），或者缺失
 * 新版 API styleType 为 3/4/5
 */
export function isLegacyStyleType(styleType: unknown): boolean {
  if (styleType == null || styleType === '') return true;
  const num = Number(styleType);
  return num === 0 || num === 1;
}

/**
 * 从 model/detail 响应构建 UniversalRenderingModelItem
 * 用于将 API 返回的模型数据适配到 selectStyle 格式
 */
export function buildSelectStyleFromModelDetail(
  modelData: any,
  _newStyleType: number
): UniversalRenderingModelItem {
  const internalType = LORA_TYPE_TO_INTERNAL[modelData.loraType] ?? 0;
  return {
    id: modelData.modelPublishId ?? modelData.id,
    name: modelData.modelPublishName ?? modelData.modelName ?? '',
    type: internalType,
    prompt: modelData.prompt ?? '',
    hidePrompt: modelData.hidePrompt ?? '',
    url: modelData.modelPublishPreview ?? modelData.preview ?? '',
    userModelId: modelData.userModelId,
    loraList: modelData.loraList ?? [],
    verList: modelData.verList ?? [],
    loraType: modelData.loraType ?? '',
    selectedVersionId: modelData.id,
  };
}

export interface LegacyMigrationResult {
  selectStyle: UniversalRenderingModelItem;
  styleType: number;
  modelId: number;
}

/**
 * 迁移旧版渲染风格到新模型体系
 *
 * 流程：
 * 1. 通过 /renderStyleTemplate/getModelId 用旧 styleId(templateId) 获取新 modelId 和 styleType
 * 2. 通过 /model/detail 验证模型是否可用并获取完整数据
 * 3. 构建标准的 selectStyle 对象
 *
 * @param styleId 旧版模板 ID（即生图参数中的 styleId）
 * @returns 迁移结果，失败时返回 null（模型已下架或接口异常）
 */
export async function migrateLegacyRenderStyle(styleId: number): Promise<LegacyMigrationResult | null> {
  try {
    const { data: migrationData } = await queryModelIdByTemplateId(styleId);
    if (!migrationData?.modelId) return null;

    const modelData = await fetchValidModelById(migrationData.modelId);
    if (!modelData) return null;

    const selectStyle = buildSelectStyleFromModelDetail(modelData, migrationData.styleType);
    rememberRenderStyle(selectStyle);

    return {
      selectStyle,
      styleType: migrationData.styleType,
      modelId: migrationData.modelId,
    };
  } catch (err) {
    logger.error('migrateLegacyRenderStyle 接口异常', { api: '/renderStyleTemplate/getModelId', params: { styleId }, error: err });
    return null;
  }
}

/** 可写入 store 的万能渲染参数片段（draftParam / reuse / 生图前兜底） */
export interface RenderParamsLike {
  module?: number;
  styleType?: unknown;
  styleId?: number | null;
  selectStyle?: UniversalRenderingModelItem | null;
  loraList?: { modelId: number; strength?: number }[];
}

/**
 * 对任意入口恢复的 render 参数做历史兼容迁移（不依赖已删除的 canvasArtWork 模块）
 *
 * 判定依据为 API 层 styleType（0/1/空），勿用 selectStyle.type（内部 0/1/2 对应 Klein 系列）
 */
export async function applyLegacyRenderParamsMigration(
  params: RenderParamsLike,
  options?: { warnOnFallback?: boolean }
): Promise<boolean> {
  if (Number(params.module) !== ModuleId.Render) return false;
  if (!isLegacyStyleType(params.styleType)) return false;

  const templateId = params.styleId;
  if (templateId == null) return false;

  const migration = await migrateLegacyRenderStyle(Number(templateId));
  if (migration) {
    params.selectStyle = migration.selectStyle;
    params.styleType = migration.styleType;
    params.styleId = migration.selectStyle.id;
    params.loraList = [{ modelId: migration.modelId }];
    return true;
  }

  if (options?.warnOnFallback === false) return false;

  const def = await getDefaultRenderStyle();
  if (!def?.id) return false;

  const selectStyle = buildSelectStyleFromModelDetail(def, 0);
  params.selectStyle = selectStyle;
  params.styleId = selectStyle.id;
  message.warn('原风格已下架，已为您切换为默认风格');
  return false;
}
